import { Button, IconButton, Tooltip, Typography } from '@neo4j-ndl/react';
import { ArrowLeftIconOutline } from '@neo4j-ndl/react/icons';
import { getFlag } from '@nx/launch-darkly-service';
import { ACTION, consoleApi, useActiveProject, useInstances, usePermissions } from '@nx/state';
import { BackButton, Page, PermissionTooltip } from '@nx/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { CreateCustomEndpointDialog } from './create-dialog';
import { CustomEndpointsTable } from './table';

export const CustomEndpoints = () => {
  const [openCreate, setOpenCreate] = useState(false);
  const project = useActiveProject();
  const { instances = [] } = useInstances({ shouldPoll: true });
  const { isAllowed } = usePermissions();
  const isAllowedToCreateCustomEndpoint = isAllowed(ACTION.CREATE, `namespaces/${project.id}/custom-endpoints`);
  const { data: endpoints = [], isLoading } = consoleApi.useListCustomEndpointsByProjectQuery(project.id, {
    pollingInterval: 10000,
  });

  const navigate = useNavigate();

  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);
  const PageContainer = isUpxConsistencyEnabled ? Page : 'div';
  const containerProps = isUpxConsistencyEnabled
    ? { contentType: 'light' as const }
    : { className: 'h-full overflow-auto p-4' };

  return (
    <>
      <PageContainer {...containerProps}>
        {!isUpxConsistencyEnabled && <BackButton to="../settings" />}
        <div className={`flex items-center justify-between ${isUpxConsistencyEnabled ? '' : 'mb-6 px-2 pt-2'}`}>
          {!isUpxConsistencyEnabled ? (
            <Typography variant="h2">Custom endpoints</Typography>
          ) : (
            <div className="flex items-center gap-3">
              <Tooltip type="simple">
                <Tooltip.Trigger hasButtonWrapper>
                  <IconButton
                    ariaLabel="Back"
                    isClean
                    onClick={() => {
                      void navigate(`/projects/${project.id}/settings`);
                    }}
                  >
                    <ArrowLeftIconOutline />
                  </IconButton>
                </Tooltip.Trigger>
                <Tooltip.Content>Back to settings</Tooltip.Content>
              </Tooltip>
              <Typography variant="h4">Custom endpoints</Typography>
            </div>
          )}
          <PermissionTooltip hasButtonWrapper hasPermission={isAllowedToCreateCustomEndpoint}>
            <Button
              isDisabled={!isAllowedToCreateCustomEndpoint}
              onClick={() => setOpenCreate(true)}
              htmlAttributes={{
                'data-testid': 'create-custom-endpoint-btn',
              }}
            >
              Create custom endpoint
            </Button>
          </PermissionTooltip>
        </div>
        <CustomEndpointsTable instances={instances} endpoints={endpoints} isLoading={isLoading} />
      </PageContainer>
      {openCreate && <CreateCustomEndpointDialog onClose={() => setOpenCreate(false)} instances={instances} />}
    </>
  );
};
